"use client";

/**
 * Mi cuenta 5.1 — perfil con stats, accesos y cierre de sesión.
 */

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";

import { BottomNav, HeaderTitulo } from "@/components/app/chrome";
import { useApp } from "@/components/app/proveedor";
import type { ZonaApp } from "@/components/app/tipos";

const ACCESOS = [
  { label: "Editar mis datos", href: "/cuenta/editar" },
  { label: "Mis direcciones", href: "/cuenta/direcciones" },
  { label: "Mis pedidos", href: "/pedidos" },
  { label: "Favoritos", href: "/favoritos" },
  { label: "Notificaciones", href: "/cuenta/notificaciones" },
];

export function CuentaCliente({ zonas }: { zonas: ZonaApp[] }) {
  const router = useRouter();
  const { usuario, zonaSlug, refrescarUsuario } = useApp();
  const [resumen, setResumen] = useState({ boots: 0, pedidos: 0, racha: 0 });

  useEffect(() => {
    if (usuario === null) router.replace("/acceso?next=/cuenta");
  }, [usuario, router]);

  const usuarioId = usuario?.id;
  useEffect(() => {
    if (!usuarioId) return;
    fetch("/api/cuenta/resumen")
      .then((r) => r.json())
      .then((data) => data.ok && setResumen(data.resumen))
      .catch(() => {});
  }, [usuarioId]);

  async function cerrarSesion() {
    await fetch("/api/auth/salir", { method: "POST" });
    await refrescarUsuario();
    router.push("/");
  }

  const zona = zonas.find((z) => z.slug === zonaSlug);
  const inicial = (usuario?.nombre || "F").charAt(0).toUpperCase();

  return (
    <>
      <HeaderTitulo titulo="Mi cuenta" />
      <main className="anim-bz-in min-h-(--alto-app) px-6 pt-[100px] pb-[62px]">
        <div className="pt-[22px] pb-[30px]">
          <div className="flex items-center gap-4">
            <span className="flex h-[68px] w-[68px] items-center justify-center rounded-3xl bg-lima font-app text-[26px] font-black text-bosque">
              {inicial}
            </span>
            <div>
              <div className="font-app text-[22px] font-black">
                {usuario?.nombre || "…"}
              </div>
              <div className="mt-[3px] text-[13px] text-tinta/55">
                +593 {usuario?.telefono ?? ""}
                {zona ? ` · ${zona.nombre}` : ""}
              </div>
            </div>
          </div>

          <div className="mt-[22px] flex gap-2.5">
            {[
              { v: resumen.boots, k: "boots tomados" },
              { v: resumen.pedidos, k: "pedidos" },
              { v: resumen.racha, k: "días con frescazo" },
            ].map((s) => (
              <div
                key={s.k}
                className="flex-1 rounded-[22px] border border-black/5 bg-white p-4"
              >
                <div className="font-app text-[22px] font-black text-verde">{s.v}</div>
                <div className="text-[11px] text-tinta/55">{s.k}</div>
              </div>
            ))}
          </div>

          <div className="mt-[22px] overflow-hidden rounded-3xl border border-black/5 bg-white">
            {ACCESOS.map((a) => (
              <Link
                key={a.href}
                href={a.href}
                className="flex items-center justify-between border-b border-black/5 px-5 py-[18px] last:border-b-0"
              >
                <span className="text-[15px]">{a.label}</span>
                <span className="text-tinta/35">›</span>
              </Link>
            ))}
            <Link
              href="/distribuidores/codigo"
              className="flex items-center justify-between px-5 py-[18px]"
            >
              <span className="text-[15px]">Tengo código de distribuidor</span>
              <span className="rounded-[14px] bg-lima/35 px-2.5 py-1 text-[11px] font-bold text-bosque">
                B2B
              </span>
            </Link>
          </div>

          <button
            type="button"
            onClick={() => void cerrarSesion()}
            className="mt-[18px] block w-full text-center text-sm text-tinta/50 underline"
          >
            Cerrar sesión
          </button>
        </div>
      </main>
      <BottomNav />
    </>
  );
}
